import { ImportPresets } from '../ImportTypes';

// The fixed column layout of a flattened mkg xml file, kept in sync
// with MKG_XML_COLUMNS in backend/imports/mkg.py so the prebuilt
// mapping always matches the rows the backend reads from the file
export const MKG_XML_COLUMNS = [
    'number',
    'main_description',
    'external_reference',
    'target_date',
    'client_number',
    'company_name',
    'contact_person_name',
    'contact_phone',
    'street',
    'house_number',
    'house_number_addition',
    'postal_code',
    'city',
    'country',
    'product_code',
    'product_name',
    'line_description',
    'quantity',
    'unit',
];

// Decode the file bytes as utf8 with a windows 1252 fallback
const decodeXmlFile = async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    try {
        return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
    } catch {
        return new TextDecoder('windows-1252').decode(buffer);
    }
};

// Get the normalized text of the first direct child with the given tag
const getXmlField = (element: Element, ...tags: string[]): string => {
    for (const tag of tags) {
        for (const child of Array.from(element.children)) {
            if (child.tagName === tag) {
                const value = (child.textContent || '').normalize('NFC').trim();
                if (value) return value;
            }
        }
    }
    return '';
};

// Get the contact person name and phone of an order as one pair, the
// delivery contact with its own phone is used when the order contact has no name
const getContactPerson = (order: Element): [string, string] => {
    const name = getXmlField(order, 't_cprs_naam');
    const phone = getXmlField(order, 't_cprs_telefoon');
    if (name) return [name, phone];
    return [
        getXmlField(order, 'Afleverpersoon.t_naam'),
        getXmlField(order, 'Afleverpersoon.cprs_telefoon') || phone,
    ];
};

// Get the extra text of an order line: the second and third description
// lines followed by the memo, dropping memo lines which repeat a description line
const getLineDescription = (line: Element): string => {
    const descriptionLines = [
        getXmlField(line, 'vorr_oms_1'),
        getXmlField(line, 'vorr_oms_2'),
        getXmlField(line, 'vorr_oms_3'),
    ];
    const knownLines = new Set(descriptionLines.filter((text) => text).map((text) => text.trim().toLowerCase()));
    const memoLines = getXmlField(line, 'vorr_memo_extern')
        .split('\n')
        .map((memoLine) => memoLine.replace(/\s+$/, ''))
        .filter((memoLine) => !knownLines.has(memoLine.trim().toLowerCase()));
    return [...descriptionLines.slice(1).filter((text) => text), ...memoLines].join('\n').trim();
};

// Format an order line as one preview line with its quantity, unit and name
const formatPreviewLine = (line: Element): string => {
    const quantity = getXmlField(line, 'vorr_order_aantal');
    const formattedQuantity = Number.isNaN(Number(quantity)) || !quantity ? quantity : String(Number(quantity));
    return [formattedQuantity, getXmlField(line, 'vorr_eenh_order'), getXmlField(line, 'vorr_oms_1')]
        .filter((part) => part)
        .join(' ');
};

// The column headers of the compact preview, one row per order
const PREVIEW_COLUMNS = [
    'import.mapping.job.number',
    'import.mapping.client.company_name',
    'import.mapping.client.contact_person_name',
    'import.mapping.location.address',
    'import.mapping.job.target_date',
    'import.mapping.item.items',
];

// Sort the order lines on their mkg line number, keeping the file order otherwise
const sortLines = (lines: Element[]): Element[] => {
    return lines
        .map((line, position) => ({ line, position }))
        .sort((a, b) => {
            const aNumber = getXmlField(a.line, 'vorr_num');
            const bNumber = getXmlField(b.line, 'vorr_num');
            const aNumeric = /^\d+$/.test(aNumber);
            const bNumeric = /^\d+$/.test(bNumber);
            if (aNumeric && bNumeric) return Number(aNumber) - Number(bNumber) || a.position - b.position;
            if (aNumeric !== bNumeric) return aNumeric ? -1 : 1;
            return a.position - b.position;
        })
        .map((entry) => entry.line);
};

// Detect and parse an mkg prodataset xml file into preset import data
export const parseMkgXml = async (file: File): Promise<ImportPresets | null> => {

    // Only xml files can be an mkg file
    if (!file.name.toLowerCase().endsWith('.xml')) return null;

    // Decode and parse the xml document
    const text = await decodeXmlFile(file);
    const document = new DOMParser().parseFromString(text, 'text/xml');

    // Reject documents with parse errors
    if (document.querySelector('parsererror')) return null;

    // Check the prodataset root and the presence of a vorh element
    const root = document.documentElement;
    const orders = Array.from(root.children).filter((child) => child.tagName === 'vorh');
    if (root.tagName !== 'ProDataSet' || orders.length === 0) return null;

    // Flatten every order into rows following the fixed column layout
    const rows: string[][] = [];
    const previewRows: string[][] = [];
    for (const order of orders) {

        // Read the contact person of the order as a name and phone pair
        const [contactPersonName, contactPhone] = getContactPerson(order);

        // Read the header fields of the order
        const headerValues = [
            getXmlField(order, 'vorh_num'),
            getXmlField(order, 'vorh_opdbev_extern'),
            getXmlField(order, 'vorh_bestelcode_extern'),
            getXmlField(order, 'vorh_dat_levering'),
            getXmlField(order, 'debi_num'),
            getXmlField(order, 'debi_num.debi_naam'),
            contactPersonName,
            contactPhone,
            getXmlField(order, 'vorh_aflever_rela.rela_adrs.adrs_straat'),
            getXmlField(order, 'vorh_aflever_rela.rela_adrs.adrs_nummer'),
            getXmlField(order, 'vorh_aflever_rela.rela_adrs.adrs_nummer_2'),
            getXmlField(order, 'vorh_aflever_rela.rela_adrs.adrs_postcode'),
            getXmlField(order, 'vorh_aflever_rela.rela_adrs.adrs_plaats'),
            getXmlField(order, 'vorh_aflever_rela.rela_adrs.land_code.land_oms'),
        ];

        // Collect the vorr line elements of the order in the mkg line number order
        const lines = sortLines(Array.from(order.children).filter((child) => child.tagName === 'vorh_vorr'));

        // Add one compact preview row for the order with its address and lines
        const houseNumber = `${headerValues[9]}${headerValues[10]}`;
        const addressLine = [`${headerValues[8]} ${houseNumber}`.trim(), `${headerValues[11]} ${headerValues[12]}`.trim()]
            .filter((part) => part)
            .join(', ');
        previewRows.push([
            headerValues[0],
            headerValues[5],
            headerValues[6],
            addressLine,
            headerValues[3],
            lines.map((line) => formatPreviewLine(line)).join('\n'),
        ]);

        // An order without lines still becomes one row with empty line values
        if (lines.length === 0) {
            rows.push([...headerValues, '', '', '', '', '']);
            continue;
        }

        // Flatten every line into a row with the header values repeated
        for (const line of lines) {

            // The first description line is the product name, the rest becomes the line description
            rows.push([
                ...headerValues,
                getXmlField(line, 'arti_code'),
                getXmlField(line, 'vorr_oms_1'),
                getLineDescription(line),
                getXmlField(line, 'vorr_order_aantal'),
                getXmlField(line, 'vorr_eenh_order'),
            ]);
        }
    }

    // Build the prebuilt mapping matching the fixed column layout
    return {
        data: rows,
        mapping: MKG_XML_COLUMNS.map((column, index) => ({ index, mapping: column })),
        skipFirstRow: false,
        preview: { columns: PREVIEW_COLUMNS, rows: previewRows },
    };
};
